import React from "react";

import { Box, Typography } from "@material-ui/core";
import clsx from "clsx";
import copy from "copy-to-clipboard";

import { MopButton } from "client/components";
import { useBranding, useRunOnce, useTranslation } from "client/hooks";
import { logAmplitudeEvent } from "client/lib/amplitude";

import { CopyKey } from "./components/CopyKey";
import { Step } from "./components/Step";
import { useStyles } from "./styles";
import type {
  CopyPasteStage,
  IntegrationStage,
  PlatformIntegration,
} from "./types";

export const General: React.FC<PlatformIntegration> = ({
  handleClickVerifyGatewayEnabled,
  merchantId,
  secretKey,
}) => {
  const { brandTitle } = useBranding();
  const [
    integrationStage,
    setIntegrationStage,
  ] = React.useState<IntegrationStage>("SignIn");

  const t = useTranslation();

  const [copyPasteStage, setCopyPasteStage] = React.useState<CopyPasteStage>(
    "None"
  );
  const classes = useStyles();

  useRunOnce(() =>
    logAmplitudeEvent("STATUS_PAGE", "integration_card_general_loaded")
  );

  return (
    <Box ml={1}>
      <Step isActive={true} isComplete={integrationStage !== "SignIn"}>
        <Typography
          className={clsx(
            classes.header,
            integrationStage === "SignIn" && classes.activeHeader
          )}
          variant="body2"
        >
          Go to your ecommerce platform payment page
        </Typography>
        <MopButton
          className={classes.shortButton}
          mopVariant={integrationStage === "SignIn" ? "primary" : "secondary"}
          onClick={() => {
            setIntegrationStage("CopyAndPaste");
            setCopyPasteStage("MerchantId");
            logAmplitudeEvent(
              "STATUS_PAGE",
              "integration_card_general_go_to_platform_page_done"
            );
          }}
        >
          {t("uiMarkAsDoneAction")}
        </MopButton>
      </Step>
      <Step
        isActive={["CopyAndPaste", "Verify", "Complete"].includes(
          integrationStage
        )}
        isComplete={["Verify", "Complete"].includes(integrationStage)}
      >
        <Typography
          className={clsx(
            classes.header,
            integrationStage === "CopyAndPaste" && classes.activeHeader
          )}
          variant="body2"
        >
          Copy and paste your Merchant ID and Secret Key in the Payment page
        </Typography>
        <Box>
          <CopyKey
            buttonText={
              ["None", "MerchantId"].includes(copyPasteStage)
                ? "Copy"
                : "Copied"
            }
            copyValue={merchantId}
            header="Merchant Id"
            isActive={integrationStage === "CopyAndPaste"}
            handleClick={() => {
              copy(merchantId);
              setCopyPasteStage("SecretKey");
              logAmplitudeEvent(
                "STATUS_PAGE",
                "integration_card_general_mid_copied"
              );
            }}
            disabled={
              integrationStage !== "CopyAndPaste" &&
              integrationStage !== "Verify"
            }
          />
          <CopyKey
            buttonText={
              ["None", "MerchantId", "SecretKey"].includes(copyPasteStage)
                ? "Copy"
                : "Copied"
            }
            copyValue={`•••••••••••`}
            handleClick={() => {
              copy(secretKey);
              setCopyPasteStage("Complete");
              setIntegrationStage("Verify");
              logAmplitudeEvent(
                "STATUS_PAGE",
                "integration_card_general_secret_key_copied"
              );
            }}
            header="Secret Key"
            isActive={integrationStage === "CopyAndPaste"}
            disabled={
              integrationStage !== "CopyAndPaste" &&
              integrationStage !== "Verify"
            }
          />
        </Box>
      </Step>
      <Step
        containerProps={{
          className: classes.lastStep,
        }}
        isActive={["Verify", "Complete"].includes(integrationStage)}
        isComplete={integrationStage === "Complete"}
      >
        <Typography
          className={clsx(
            classes.header,
            integrationStage === "Verify" && classes.activeHeader
          )}
          variant="body2"
        >
          Verify that {brandTitle} is active
        </Typography>
        <Box>
          <MopButton
            className={classes.shortButton}
            mopVariant={integrationStage === "Verify" ? "primary" : "secondary"}
            onClick={() => {
              handleClickVerifyGatewayEnabled();
              setIntegrationStage("Complete");
              logAmplitudeEvent(
                "STATUS_PAGE",
                "integration_card_general_complete"
              );
            }}
            disabled={integrationStage !== "Verify"}
          >
            Verify
          </MopButton>
        </Box>
      </Step>
    </Box>
  );
};
